import { useCallback, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";

interface UsePersistentStringOptions<T> {
  paramName?: string;
  storageKey?: string;
  defaultValue?: T;
  validate?: (value) => boolean;
}

export default function usePersistentString<T extends string>(
  options: UsePersistentStringOptions<T>,
) {
  const { paramName, storageKey, defaultValue, validate } = options;

  const { search, pathname } = useLocation();

  const history = useHistory();

  const [state, setState] = useState<T>(() => {
    if (paramName) {
      const value = getParamValue(search, paramName);
      if (value) {
        return decodeFromBase64(value) as T;
      }
    }
    if (storageKey) {
      const value = getStorageValue(storageKey);
      if (validate && !validate(value)) {
        return defaultValue as T;
      }
      if (value) {
        return decodeFromBase64(value) as T;
      }
    }
    return defaultValue as T;
  });

  const handleSetState = useCallback(
    (value: T | ((prev: T) => T)) => {
      const nextState = typeof value === "function" ? value(state) : value;
      setState(nextState);

      const urlSearchParams = new URLSearchParams(search);
      if (paramName) {
        if (!nextState) {
          urlSearchParams.delete(paramName);
        } else {
          const formatted = encodeToBase64(nextState);
          if (formatted) {
            urlSearchParams.set(paramName, formatted);
            const params = urlSearchParams.toString();
            const url = `${pathname}?${params}`;
            history.replace(url);
          }
        }
      }
      if (storageKey) {
        if (!nextState) {
          localStorage.removeItem(storageKey);
        } else {
          const formatted = encodeToBase64(nextState);
          if (formatted) {
            localStorage.setItem(storageKey, formatted);
          }
        }
      }
    },
    [search, paramName, storageKey, pathname, history, state],
  );

  return [state, handleSetState] as const;
}

function getStorageValue(key: string) {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    return undefined;
  }
}

function getParamValue(search: string, name: string) {
  try {
    const urlSearchParams = new URLSearchParams(search);
    return urlSearchParams.get(name);
  } catch (error) {
    return undefined;
  }
}

function encodeToBase64(str: string) {
  try {
    return encodeURIComponent(str);
  } catch (error) {
    return undefined;
  }
}

function decodeFromBase64(str: string) {
  try {
    return decodeURIComponent(str);
  } catch (error) {
    return undefined;
  }
}
